import {
  CellId,
  CellLocation,
  ClientAnalyticsEventType,
  DataBrowserDialogOpenedSource,
  DataBrowserTab,
  DataConnectionId,
  DataSourceTableId,
  KernelState,
  START_APP_SESSION,
  SemanticDatasetName,
  SemanticProjectId,
} from "@hex/common";
import { useCallback } from "react";

import { useAppSessionGetter } from "../../../appsession-multiplayer/state-hooks/appSessionStateHooks.js";
import {
  LocalStorageKeys,
  useLocalStorageSetter,
} from "../../../hooks/useLocalStorage.js";
import {
  SessionStorageKeys,
  useSessionStorageSetter,
} from "../../../hooks/useSessionStorage.js";
import { useToggleState } from "../../../hooks/useToggleState.js";
import { useDispatch } from "../../../redux/hooks.js";
import { setInsertLocation } from "../../../redux/slices/dataBrowserSlice.js";
import {
  collapseAllNodes,
  setNodeSelected,
} from "../../../redux/slices/schemaTreeBrowserSlice.js";
import { useAppSessionAOContext } from "../../../util/appSessionAOContext.js";
import { useDialog } from "../../../util/dialogs.js";
import { trackEvent } from "../../../util/trackEvent.js";
import { useDataBrowserRecommendedView } from "../data-browser-recommended-view/hooks/useDataBrowserRecommendedView.js";
import {
  DataBrowserRecommendedViewPanel,
  DataBrowserRecommendedViewSelection,
} from "../data-browser-recommended-view/utils/recommendedView.js";

export const getDataBrowserDialogKey = ({
  cellId,
  source,
}: {
  cellId?: CellId | null;
  source?: DataBrowserDialogOpenedSource;
}): string => {
  let suffix = cellId ? `-${cellId}` : "";

  // right now only the data browser when opened to join data needs its own key suffix
  suffix +=
    source === DataBrowserDialogOpenedSource.EXPLORE_JOIN_DATA
      ? "-join-data"
      : "";

  return `data-browser${suffix}`;
};

/**
 * This wraps the generic useDialog hook - this will also attempt to start the
 * kernel if kernel is stopped when opening the data browser.
 */

export const useDataBrowserDialog = (args: {
  /**
   * The cell id that the data browser was opened from.
   */
  selectedCellId?: CellId;
  /**
   * @default global
   */
  location?: CellLocation;
  /**
   * For differentiating where the data browser was opened and tracking purposes
   */
  source: DataBrowserDialogOpenedSource;
  /**
   * Set to true to use local React state for open/close instead of URL query params
   */
  controlled?: boolean;
}): {
  /**
   * Use this if you want to open a specific table in the data browser dialog
   * from outside the data browser. This currently only supports opening the
   * table in the "data" tab in the "browser" tree view. Otherwise, use
   * @openDataBrowser to open the data browser.
   */
  openTableInDataBrowser: (tableInfo: {
    tableId: DataSourceTableId;
    dataConnectionId: DataConnectionId;
    idPath?: string[];
  }) => void;
  openDataBrowser: () => void;
  openDataBrowserToTab: (tab: DataBrowserTab) => void;
  closeDataBrowser: (args?: { navigate: boolean }) => void;
  openDataBrowserToSemanticDataset: (params: {
    semanticDatasetName: SemanticDatasetName;
    semanticProjectId: SemanticProjectId;
  }) => void;
  openDataBrowserToRecommendedView: (
    panel: DataBrowserRecommendedViewPanel,
    selection?: DataBrowserRecommendedViewSelection,
  ) => void;
  isOpen: boolean;
} => {
  const { controlled = false, location, selectedCellId, source } = args;
  const {
    closeDialog: closeDialogURL,
    isOpen: isOpenURL,
    openDialog: openDialogURL,
  } = useDialog(getDataBrowserDialogKey({ cellId: selectedCellId, source }));
  const [
    isOpenState,
    _setIsOpenState,
    { setFalse: closeInternal, setTrue: openInternal },
  ] = useToggleState(false);

  const isOpen = controlled ? isOpenState : isOpenURL;
  const openDataBrowserFn = controlled ? openInternal : openDialogURL;
  const closeDataBrowser = controlled ? closeInternal : closeDialogURL;

  const getKernelNeedsRestart = useAppSessionGetter({
    selector: (appSession) =>
      Boolean(appSession && appSession.kernelState === KernelState.STOPPED),
    safe: true,
  });

  const { dispatchAO: dispatchAppSessionAO } = useAppSessionAOContext();
  const setSidebarConnectionId = useLocalStorageSetter(
    LocalStorageKeys.SCHEMA_BROWSER_CONNECTION(),
  );
  const setCurrentTab = useSessionStorageSetter(
    SessionStorageKeys.DATA_BROWSER_VIEW(),
  );
  const setSemanticTabPage = useSessionStorageSetter(
    SessionStorageKeys.DATA_BROWSER_SEMANTIC_TAB_PAGE(),
  );

  const dispatch = useDispatch();

  const openTableInDataBrowser = useCallback(
    (tableInfo?: {
      tableId: DataSourceTableId;
      dataConnectionId: DataConnectionId;
      // if specified, this will expand the schema browser nodes included in
      // this id path and collapse all others
      idPath?: string[];
    }) => {
      if (tableInfo) {
        const { dataConnectionId, idPath, tableId } = tableInfo;

        setCurrentTab(DataBrowserTab.DATA);
        setSidebarConnectionId(dataConnectionId);

        dispatch(
          collapseAllNodes({
            treeType: "browser",
            dataConnectionId,
          }),
        );
        dispatch(
          setNodeSelected({
            dataConnectionId,
            nodeId: tableId,
            isSelected: true,
            treeType: "browser",
            nodeType: "table",
            idPath,
          }),
        );
      }
      trackEvent(ClientAnalyticsEventType.DATA_BROWSER_DIALOG_OPENED, {
        dataBrowserOpenedSource: source,
      });
      openDataBrowserFn();

      dispatch(setInsertLocation(location ?? { type: "global" }));

      if (getKernelNeedsRestart()) {
        dispatchAppSessionAO(START_APP_SESSION.create());
      }
    },
    [
      source,
      openDataBrowserFn,
      dispatch,
      location,
      getKernelNeedsRestart,
      setCurrentTab,
      setSidebarConnectionId,
      dispatchAppSessionAO,
    ],
  );

  const openDataBrowserClientCallback = useCallback(() => {
    openTableInDataBrowser();
  }, [openTableInDataBrowser]);

  const openDataBrowserToTab = useCallback(
    (tab: DataBrowserTab) => {
      setCurrentTab(tab);
      openDataBrowserClientCallback();
    },
    [openDataBrowserClientCallback, setCurrentTab],
  );

  const openDataBrowserToSemanticDataset = useCallback(
    (params: {
      semanticDatasetName: SemanticDatasetName;
      semanticProjectId: SemanticProjectId;
    }) => {
      const { semanticDatasetName, semanticProjectId } = params;
      setCurrentTab(DataBrowserTab.SEMANTIC_LAYER);
      openDataBrowserFn();
      setSemanticTabPage({
        semanticProjectId,
        semanticDatasetName,
      });
    },
    [setCurrentTab, setSemanticTabPage, openDataBrowserFn],
  );

  const { navigateToPanel: navigateToRecommendedViewPanel } =
    useDataBrowserRecommendedView();

  const openDataBrowserToRecommendedView = useCallback(
    (
      panel: DataBrowserRecommendedViewPanel,
      selection?: DataBrowserRecommendedViewSelection,
    ) => {
      setCurrentTab(DataBrowserTab.RECOMMENDED);
      navigateToRecommendedViewPanel(panel, selection);
      openDataBrowserFn();
    },
    [navigateToRecommendedViewPanel, setCurrentTab, openDataBrowserFn],
  );

  return {
    openTableInDataBrowser,
    openDataBrowser: openDataBrowserClientCallback,
    openDataBrowserToTab,
    openDataBrowserToSemanticDataset,
    openDataBrowserToRecommendedView,
    closeDataBrowser,
    isOpen,
  };
};
